import React, { useEffect, useState, useCallback } from "react"
import {
  Card,
  CardBody,
  Col,
  Row,
  Container,
  Table,
  Button,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import {
  getWorkspaceAuditDashboard,
  getWorkspaceAuditDetail,
  getWorkspaceAuditList,
} from "helpers/backend_helper"
import moment from "moment"

// Format a byte count into a human-readable string (B/KB/MB/GB/TB).
const formatBytes = bytes => {
  const num = Number(bytes)
  if (bytes == null || isNaN(num) || num === 0) return "0 B"
  const units = ["B", "KB", "MB", "GB", "TB", "PB"]
  const i = Math.floor(Math.log(Math.abs(num)) / Math.log(1024))
  const idx = Math.min(i, units.length - 1)
  const value = num / Math.pow(1024, idx)
  return `${value.toFixed(value >= 10 || idx === 0 ? 0 : 1)} ${units[idx]}`
}

// Defensive response unwrapping: API returns mixed casing.
const unwrap = res => res?.data ?? res?.Data ?? null
const isSuccess = res => res?.statusCode === 200 || res?.StatusCode === 200

// Read a field from an object trying several casings.
const pick = (obj, ...keys) => {
  if (!obj) return undefined
  for (const k of keys) {
    if (obj[k] != null && obj[k] !== "") return obj[k]
  }
  return undefined
}

const fmtDate = val => {
  if (!val) return "-"
  const m = moment(val)
  return m.isValid() ? m.format("DD MMM YYYY") : val
}

const fmtPercent = val => {
  if (val == null || val === "") return "-"
  const n = Number(val)
  return isNaN(n) ? "-" : `${n.toFixed(n % 1 === 0 ? 0 : 1)}%`
}

const StatCard = ({ icon, label, value, color = "primary" }) => (
  <Col xl={3} md={6} className="mb-3">
    <Card className="mb-0 h-100">
      <CardBody>
        <div className="d-flex align-items-center">
          <div className={`avatar-sm me-3`}>
            <span
              className={`avatar-title rounded-circle bg-${color} bg-soft text-${color} font-size-20`}
            >
              <i className={`mdi ${icon}`} />
            </span>
          </div>
          <div className="flex-grow-1">
            <p className="text-muted mb-1 font-size-13">{label}</p>
            <h5 className="mb-0">{value}</h5>
          </div>
        </div>
      </CardBody>
    </Card>
  </Col>
)

const DetailRow = ({ label, value }) => (
  <Col md={4} className="mb-2">
    <div className="text-muted font-size-12">{label}</div>
    <div className="font-size-14">{value != null && value !== "" ? value : "-"}</div>
  </Col>
)

const SubTable = ({ title, columns, rows, renderRow }) => (
  <div className="mb-4">
    <h6 className="mb-2">{title}</h6>
    <div className="table-responsive">
      <Table bordered hover responsive className="mb-0 font-size-13">
        <thead className="table-light">
          <tr>
            {columns.map(c => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows && rows.length > 0 ? (
            rows.map((row, idx) => renderRow(row, idx))
          ) : (
            <tr>
              <td colSpan={columns.length} className="text-center text-muted py-3">
                No records
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  </div>
)

const WorkspaceAuditExecutive = () => {
  document.title = "Workspace Audit Summary | FlexiTracBoard"

  const [dashboard, setDashboard] = useState(null)
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(false)

  const [modalOpen, setModalOpen] = useState(false)
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [dashRes, listRes] = await Promise.all([
        getWorkspaceAuditDashboard({}),
        getWorkspaceAuditList({ page: 1, pagesize: 100 }),
      ])

      if (isSuccess(dashRes)) {
        setDashboard(unwrap(dashRes) || null)
      } else {
        setDashboard(null)
      }

      if (isSuccess(listRes)) {
        const rows = unwrap(listRes)
        setList(Array.isArray(rows) ? rows : [])
      } else {
        setList([])
      }
    } catch {
      setDashboard(null)
      setList([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const openDetail = async id => {
    setModalOpen(true)
    setDetail(null)
    setDetailLoading(true)
    try {
      const res = await getWorkspaceAuditDetail({ id })
      setDetail(isSuccess(res) ? unwrap(res) : null)
    } catch {
      setDetail(null)
    } finally {
      setDetailLoading(false)
    }
  }

  const closeModal = () => {
    setModalOpen(false)
    setDetail(null)
  }

  // Dashboard scalar helpers (camelCase from API, unwrap defensively).
  const d = dashboard || {}
  const dVal = (...keys) => pick(d, ...keys)

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Admin" breadcrumbItem="Workspace Audit Summary" />

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <>
              {/* SUMMARY STAT CARDS */}
              <Row>
                <StatCard
                  icon="mdi-clipboard-check-outline"
                  label="Total Audits"
                  value={dVal("totalAudits", "totalaudits") ?? 0}
                  color="primary"
                />
                <StatCard
                  icon="mdi-laptop"
                  label="Machines"
                  value={dVal("totalMachines", "totalmachines") ?? 0}
                  color="info"
                />
                <StatCard
                  icon="mdi-harddisk"
                  label="Total Storage"
                  value={formatBytes(dVal("totalStorageBytes", "totalstoragebytes"))}
                  color="success"
                />
                <StatCard
                  icon="mdi-harddisk-plus"
                  label="Free Space"
                  value={formatBytes(dVal("totalFreeBytes", "totalfreebytes"))}
                  color="warning"
                />
                <StatCard
                  icon="mdi-file-multiple-outline"
                  label="Total Files"
                  value={(dVal("totalFiles", "totalfiles") ?? 0).toLocaleString()}
                  color="primary"
                />
                <StatCard
                  icon="mdi-source-repository"
                  label="Repositories"
                  value={dVal("totalRepositories", "totalrepositories") ?? 0}
                  color="info"
                />
                <StatCard
                  icon="mdi-shield-check-outline"
                  label="Avg Controlled %"
                  value={fmtPercent(
                    dVal("avgControlledPercentage", "avgcontrolledpercentage")
                  )}
                  color="success"
                />
                <StatCard
                  icon="mdi-cloud-outline"
                  label="OneDrive Size"
                  value={formatBytes(dVal("totalOneDriveBytes", "totalonedrivebytes"))}
                  color="warning"
                />
              </Row>

              <Row className="mb-3">
                <Col>
                  <span className="text-muted font-size-13">
                    Latest assessment:{" "}
                    <strong>
                      {fmtDate(
                        dVal("latestAssessmentDate", "latestassessmentdate")
                      )}
                    </strong>
                    {"  |  Documents: "}
                    <strong>
                      {formatBytes(dVal("totalDocumentBytes", "totaldocumentbytes"))}
                    </strong>
                    {"  |  Artifacts: "}
                    <strong>
                      {formatBytes(dVal("totalArtifactBytes", "totalartifactbytes"))}
                    </strong>
                  </span>
                </Col>
              </Row>

              {/* AUDIT LIST */}
              <Card>
                <CardBody>
                  <h5 className="mb-3">All Workspace Audits</h5>
                  <div className="table-responsive">
                    <Table bordered hover responsive className="mb-0 font-size-13 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Machine</th>
                          <th>User</th>
                          <th>OS</th>
                          <th>Assessment Date</th>
                          <th className="text-end">Total Size</th>
                          <th className="text-end">Files</th>
                          <th className="text-end">Repos</th>
                          <th className="text-end">Controlled %</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="text-center text-muted py-4">
                              No workspace audits found.
                            </td>
                          </tr>
                        ) : (
                          list.map(row => {
                            const id = pick(row, "id", "Id")
                            return (
                              <tr key={id}>
                                <td>{pick(row, "machinename", "machineName") || "-"}</td>
                                <td>{pick(row, "username", "userName") || "-"}</td>
                                <td>{pick(row, "osdescription", "osDescription") || "-"}</td>
                                <td>
                                  {fmtDate(pick(row, "assessmentdate", "assessmentDate"))}
                                </td>
                                <td className="text-end">
                                  {formatBytes(pick(row, "totalsizebytes", "totalSizeBytes"))}
                                </td>
                                <td className="text-end">
                                  {(pick(row, "totalfiles", "totalFiles") ?? 0).toLocaleString()}
                                </td>
                                <td className="text-end">
                                  {pick(row, "repositorycount", "repositoryCount") ?? 0}
                                </td>
                                <td className="text-end">
                                  <Badge color="info" pill>
                                    {fmtPercent(
                                      pick(row, "controlledpercentage", "controlledPercentage")
                                    )}
                                  </Badge>
                                </td>
                                <td className="text-center">
                                  <Button
                                    size="sm"
                                    color="primary"
                                    outline
                                    onClick={() => openDetail(id)}
                                  >
                                    <i className="mdi mdi-eye-outline me-1" />
                                    View
                                  </Button>
                                </td>
                              </tr>
                            )
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </>
          )}
        </Container>
      </div>

      {/* DETAIL MODAL */}
      <Modal size="xl" isOpen={modalOpen} toggle={closeModal} scrollable>
        <ModalHeader toggle={closeModal}>
          {detail
            ? `${pick(detail, "machinename", "machineName") || "Audit"} — Detail`
            : "Audit Detail"}
        </ModalHeader>
        <ModalBody>
          {detailLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : !detail ? (
            <div className="text-center text-muted py-5">
              No detail available for this audit.
            </div>
          ) : (
            <DetailBody detail={detail} />
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}

const DetailBody = ({ detail }) => {
  const g = (...keys) => pick(detail, ...keys)

  const categories = g("categories") || []
  const repositories = g("repositories") || []
  const onedrivelocations = g("onedrivelocations", "oneDriveLocations") || []
  const folders = g("folders") || []
  const largestfiles = g("largestfiles", "largestFiles") || []

  return (
    <div>
      {/* OVERVIEW */}
      <Card className="mb-3">
        <CardBody>
          <h6 className="mb-3">Machine Overview</h6>
          <Row>
            <DetailRow label="Machine" value={g("machinename", "machineName")} />
            <DetailRow label="User" value={g("username", "userName")} />
            <DetailRow label="Operating System" value={g("osdescription", "osDescription")} />
            <DetailRow
              label="Assessment Date"
              value={fmtDate(g("assessmentdate", "assessmentDate"))}
            />
            <DetailRow label="Source" value={g("source")} />
            <DetailRow label="Local Drives" value={g("localdrivecount", "localDriveCount")} />
            <DetailRow
              label="Created At"
              value={fmtDate(g("createdat", "createdAt"))}
            />
          </Row>
        </CardBody>
      </Card>

      {/* STORAGE TOTALS */}
      <Card className="mb-3">
        <CardBody>
          <h6 className="mb-3">Storage Totals</h6>
          <Row>
            <DetailRow
              label="Total Size"
              value={formatBytes(g("totalsizebytes", "totalSizeBytes"))}
            />
            <DetailRow
              label="Disk Capacity"
              value={formatBytes(g("totaldiskcapacitybytes", "totalDiskCapacityBytes"))}
            />
            <DetailRow
              label="Free Disk"
              value={formatBytes(g("totalfreediskbytes", "totalFreeDiskBytes"))}
            />
            <DetailRow
              label="Total Files"
              value={(g("totalfiles", "totalFiles") ?? 0).toLocaleString()}
            />
            <DetailRow
              label="Total Directories"
              value={(g("totaldirectories", "totalDirectories") ?? 0).toLocaleString()}
            />
            <DetailRow
              label="Repositories"
              value={g("repositorycount", "repositoryCount")}
            />
          </Row>
        </CardBody>
      </Card>

      {/* CONTROLLED VS UNCONTROLLED */}
      <Card className="mb-3">
        <CardBody>
          <h6 className="mb-3">Source Control Coverage</h6>
          <Row>
            <DetailRow
              label="Controlled %"
              value={fmtPercent(g("controlledpercentage", "controlledPercentage"))}
            />
            <DetailRow
              label="Controlled Files"
              value={(g("controlledfilecount", "controlledFileCount") ?? 0).toLocaleString()}
            />
            <DetailRow
              label="Controlled Size"
              value={formatBytes(g("controlledsizebytes", "controlledSizeBytes"))}
            />
            <DetailRow
              label="Uncontrolled Files"
              value={(g("uncontrolledfilecount", "uncontrolledFileCount") ?? 0).toLocaleString()}
            />
            <DetailRow
              label="Uncontrolled Size"
              value={formatBytes(g("uncontrolledsizebytes", "uncontrolledSizeBytes"))}
            />
            <DetailRow
              label="Notable Uncontrolled Areas"
              value={g("notableuncontrolledareas", "notableUncontrolledAreas")}
            />
          </Row>
        </CardBody>
      </Card>

      {/* DOCUMENTS & ARTIFACTS */}
      <Card className="mb-3">
        <CardBody>
          <h6 className="mb-3">Documents &amp; Artifacts</h6>
          <Row>
            <DetailRow
              label="Document Files"
              value={(g("documentfilecount", "documentFileCount") ?? 0).toLocaleString()}
            />
            <DetailRow
              label="Document Size"
              value={formatBytes(g("documentsizebytes", "documentSizeBytes"))}
            />
            <DetailRow
              label="Document Locations"
              value={g("documentlocations", "documentLocations")}
            />
            <DetailRow
              label="Artifact Size"
              value={formatBytes(g("artifactsizebytes", "artifactSizeBytes"))}
            />
            <DetailRow
              label="Artifact Types"
              value={g("artifacttypes", "artifactTypes")}
            />
          </Row>
        </CardBody>
      </Card>

      {/* ONEDRIVE */}
      <Card className="mb-3">
        <CardBody>
          <h6 className="mb-3">OneDrive</h6>
          <Row>
            <DetailRow label="Synced" value={g("onedrivesynced", "oneDriveSynced")} />
            <DetailRow
              label="Account Types"
              value={g("onedriveaccounttypes", "oneDriveAccountTypes")}
            />
            <DetailRow
              label="OneDrive Files"
              value={(g("onedrivefilecount", "oneDriveFileCount") ?? 0).toLocaleString()}
            />
            <DetailRow
              label="OneDrive Size"
              value={formatBytes(g("onedrivesizebytes", "oneDriveSizeBytes"))}
            />
            <DetailRow
              label="Git Repos in OneDrive"
              value={g("onedrivegitrepos", "oneDriveGitRepos")}
            />
          </Row>
        </CardBody>
      </Card>

      {/* NOTES */}
      <Card className="mb-3">
        <CardBody>
          <h6 className="mb-3">Notes</h6>
          <Row>
            <DetailRow label="Limiting Notes" value={g("limitingnotes", "limitingNotes")} />
            <DetailRow label="Storage Concerns" value={g("storageconcerns", "storageConcerns")} />
          </Row>
        </CardBody>
      </Card>

      {/* SUB-TABLES */}
      <SubTable
        title="Categories"
        columns={["Category", "Files", "Size", "% of Total"]}
        rows={categories}
        renderRow={(row, idx) => (
          <tr key={idx}>
            <td>{pick(row, "category") || "-"}</td>
            <td className="text-end">
              {(pick(row, "filecount", "fileCount") ?? 0).toLocaleString()}
            </td>
            <td className="text-end">{formatBytes(pick(row, "sizebytes", "sizeBytes"))}</td>
            <td className="text-end">{fmtPercent(pick(row, "percentage"))}</td>
          </tr>
        )}
      />

      <SubTable
        title="Repositories"
        columns={[
          "Name",
          "Path",
          "Branch",
          "Remote",
          "Size",
          "Files",
          "Clean",
          "In OneDrive",
          "Last Commit",
        ]}
        rows={repositories}
        renderRow={(row, idx) => (
          <tr key={idx}>
            <td>{pick(row, "name") || "-"}</td>
            <td>{pick(row, "rootpath", "rootPath") || "-"}</td>
            <td>{pick(row, "branch") || "-"}</td>
            <td>{pick(row, "remoteurl", "remoteUrl") || "-"}</td>
            <td className="text-end">{formatBytes(pick(row, "sizebytes", "sizeBytes"))}</td>
            <td className="text-end">
              {(pick(row, "filecount", "fileCount") ?? 0).toLocaleString()}
            </td>
            <td className="text-center">
              {pick(row, "isclean", "isClean") ? "Yes" : "No"}
            </td>
            <td className="text-center">
              {pick(row, "isinsideonedrive", "isInsideOneDrive") ? "Yes" : "No"}
            </td>
            <td>{pick(row, "lastcommitsummary", "lastCommitSummary") || "-"}</td>
          </tr>
        )}
      />

      <SubTable
        title="OneDrive Locations"
        columns={["Root Path", "Account", "Size", "Files", "Has Git Repos", "Content Types"]}
        rows={onedrivelocations}
        renderRow={(row, idx) => (
          <tr key={idx}>
            <td>{pick(row, "rootpath", "rootPath") || "-"}</td>
            <td>{pick(row, "account") || "-"}</td>
            <td className="text-end">{formatBytes(pick(row, "sizebytes", "sizeBytes"))}</td>
            <td className="text-end">
              {(pick(row, "filecount", "fileCount") ?? 0).toLocaleString()}
            </td>
            <td className="text-center">
              {pick(row, "hasgitrepos", "hasGitRepos") ? "Yes" : "No"}
            </td>
            <td>{pick(row, "contenttypes", "contentTypes") || "-"}</td>
          </tr>
        )}
      />

      <SubTable
        title="Folders"
        columns={["Path", "Size", "Files", "Directories", "Type"]}
        rows={folders}
        renderRow={(row, idx) => (
          <tr key={idx}>
            <td>{pick(row, "path") || "-"}</td>
            <td className="text-end">{formatBytes(pick(row, "sizebytes", "sizeBytes"))}</td>
            <td className="text-end">
              {(pick(row, "filecount", "fileCount") ?? 0).toLocaleString()}
            </td>
            <td className="text-end">
              {(pick(row, "directorycount", "directoryCount") ?? 0).toLocaleString()}
            </td>
            <td>{pick(row, "foldertype", "folderType") || "-"}</td>
          </tr>
        )}
      />

      <SubTable
        title="Largest Files"
        columns={["Path", "Size", "Type"]}
        rows={largestfiles}
        renderRow={(row, idx) => (
          <tr key={idx}>
            <td>{pick(row, "path") || "-"}</td>
            <td className="text-end">{formatBytes(pick(row, "sizebytes", "sizeBytes"))}</td>
            <td>{pick(row, "filetype", "fileType") || "-"}</td>
          </tr>
        )}
      />
    </div>
  )
}

export default WorkspaceAuditExecutive
